import type { MouseEvent, ReactElement, ReactNode } from "react";
import { memo, useCallback } from "react";

import { useDispatch } from "react-redux";

import { FilterSearchBox } from "app/pages/sales-intelligence/common-components/filters-panel/components/FilterSearchBox/FilterSearchBox";
import { CategorizedFiltersList } from "app/pages/sales-intelligence/common-components/filters-panel/components/FiltersList/CategorizedFiltersList";
import type { IPlainObject } from "app/pages/sales-intelligence/types/plainObject";

import { SearchResultsGuard } from "../../components/SearchResultsGuard/SearchResultsGuard";
import { useFilterSearchTracking } from "../../hooks/useFilterSearchTracking";
import type { IFilterManagerModel } from "../../services/filters-manager/types";
import { setSearchQuery } from "../../store/action-creators";
import type { EnrichedCategorizedGroup, FilterCategory } from "../../types/category";
import type { IFilterInstance } from "../../types/filterInstance";

type Props<T extends IPlainObject = IPlainObject> = {
    filtersManager: IFilterManagerModel<T>;
    permanentFilters: readonly IFilterInstance<T>[];
    groups: readonly EnrichedCategorizedGroup<IFilterInstance<T>>[];
    renderFilterCard: (config: IFilterInstance<T>) => ReactElement;
    isLoading?: boolean;
    withSearch?: boolean;
    footerSlot?: ReactNode;
    belowSearchSlot?: ReactNode;
    onOpenCategoryPanel?: (category: FilterCategory, event?: MouseEvent<HTMLElement>) => void;
};

const FiltersListContainerBase = <T extends IPlainObject = IPlainObject>({
    filtersManager,
    permanentFilters,
    groups,
    renderFilterCard,
    isLoading,
    withSearch,
    footerSlot,
    belowSearchSlot,
    onOpenCategoryPanel,
}: Props<T>) => {
    const dispatch = useDispatch();

    const { onTrackSearchFocus, onTrackSearchChange } = useFilterSearchTracking(filtersManager);

    const handleSearchChange = useCallback(
        (query: string) => {
            onTrackSearchChange(query);

            dispatch(setSearchQuery(query));
        },
        [onTrackSearchChange],
    );

    return (
        <>
            {withSearch ? (
                <FilterSearchBox
                    onChange={handleSearchChange}
                    onFocus={onTrackSearchFocus}
                />
            ) : null}

            {belowSearchSlot}

            <SearchResultsGuard
                filtersManager={filtersManager}
                isLoading={isLoading}
            >
                <CategorizedFiltersList
                    permanentFilters={permanentFilters}
                    groups={groups}
                    renderFilterCard={renderFilterCard}
                    isLoading={isLoading}
                    footerSlot={footerSlot}
                    onOpenCategoryPanel={onOpenCategoryPanel}
                />
            </SearchResultsGuard>
        </>
    );
};

// Memoized so FilterPanelContainer-local re-renders (overlay open/close, transform origin,
// animation gate, redux panel toggle) skip the list entirely
const FiltersListContainer = memo(FiltersListContainerBase) as typeof FiltersListContainerBase;

export default FiltersListContainer;
